import { Dispatch, ReactElement, SetStateAction } from "react"

import ApplicationOrchestrator from "./applicationOrchestrator"
import { unrecognisedError } from "../../../../errors/recognisedErrors"
import useGetSchema from "../../../../services/middleware/hooks/singleApplication/useGetSchema"
import { ILaunchDraftMapped } from "../../../../services/middleware/types/draftApplication/launchDraftTypes"
import ErrorDisplay from "../../../fallback/errorDisplay"
import CreateApplicationSkeleton from "../../../loading/skeletons/createApplicationSkeleton"
import ApplicationProvider from "../applicationProvider"

interface IApplicationGeneratorProps {
    lcts: string[]
    setDisplaySelectLct: Dispatch<SetStateAction<boolean>>
    isDraftMode?: boolean
    draftData?: ILaunchDraftMapped
}

/**
 * A core component that serves as the foundation for generating dynamic application forms.
 * It acts as a smart controller that fetches the required schema from the API and orchestrates
 * the form generation process. In draft mode, it can populate the form with existing draft data,
 * while in normal mode it generates a fresh application form based on the provided LCT types.
 *
 * @component
 * @param {IApplicationGeneratorProps} props - Component props
 * @param {string[]} props.lcts - Array of LCT identifiers to fetch the schema for
 * @param {Dispatch<SetStateAction<boolean>>} props.setDisplaySelectLct - Function to toggle LCT selection display
 * @param {boolean} [props.isDraftMode=false] - Flag to indicate if the application is in draft mode
 * @param {ILaunchDraftMapped} [props.draftData] - Draft application data when in draft mode
 * @returns {ReactElement} Rendered component
 */
const ApplicationGenerator = ({
    lcts,
    setDisplaySelectLct,
    isDraftMode = false,
    draftData,
}: IApplicationGeneratorProps): ReactElement => {
    // Always fetch the schema, but conditionally use results based on draft mode
    const schemaResult = useGetSchema(lcts)

    // Choose data source based on mode
    const dataSource =
        isDraftMode && draftData
            ? { data: draftData, isLoading: false, error: null }
            : schemaResult

    const { data, isLoading, error } = dataSource

    if (isLoading) return <CreateApplicationSkeleton />
    if (error) return <ErrorDisplay error={error} />
    if (!data) return <ErrorDisplay error={unrecognisedError()} />

    return (
        <ApplicationProvider
            rawApplicationSpec={data.rawApplicationSpec}
            applicationStepIndexMap={data.applicationStepIndexMap}
            applicationForms={data.applicationForms}
            formTitlesIndexMap={data.formTitlesIndexMap}
            onClickBackFirstStep={(): void => setDisplaySelectLct(true)}
            fixedInputs={data.fixedInputs}
            lcts={lcts}
            isDraftMode={isDraftMode}
            draftId={isDraftMode && draftData ? draftData.draftId : undefined}
            defaultFormData={isDraftMode && draftData ? draftData.formData : {}}
            defaultSupplementingInfo={
                isDraftMode && draftData ? draftData.supplementingInfo : {}
            }
            // jurisdiction={jurisdiction}
            defaultCompletionStatus={
                isDraftMode && draftData ? draftData.completionStatus : 0
            }
        >
            <ApplicationOrchestrator />
        </ApplicationProvider>
    )
}

ApplicationGenerator.defaultProps = {
    isDraftMode: false,
    draftData: undefined,
}

export default ApplicationGenerator
